/* =============================================================================
 * M.M.LaFleur — Editorial layer behaviour
 *
 * Everything here is written as delegated document-level listeners so it keeps
 * working after Barba swaps the <main> container. The only per-page work is in
 * mmInit(), which is idempotent and re-runs on navigation.
 *
 * SINGLE REGISTRATION
 * -------------------
 * Platter re-executes this file around a Barba container swap. Without a guard
 * every listener below is bound a second time after the first soft navigation,
 * and a third time after the next. Anything idempotent survives that — size
 * chips still select, quick add still opens — but every TOGGLE dies, because it
 * fires an even number of times per click and lands back where it started.
 *
 * That is what killed the PLP filter drawer, the facet headers and the PDP
 * accordions after navigating between collections.
 * ========================================================================== */

(function () {
  "use strict";

  if (window.__mmEditorialBound) {
    /* Re-injected after a container swap. The listeners from the first run are
       still live, so only the per-page work needs repeating.

       v4.7.8 site speed: this file is also included by most mm- sections, so
       a single page load executes it up to seven times, and each copy used to
       re-run mmInit() in full (including a /cart.js request). Repeats that
       land within 800ms of the last init are now skipped: that is the same
       page, already initialised. A real navigation is still covered by the
       Barba hook below and by the first re-injected copy. */
    if (typeof window.mmInit === "function" &&
        Date.now() - (window.__mmInitAt || 0) > 800) {
      window.mmInit();
    }
    return;
  }
  window.__mmEditorialBound = true;

  /* Captured synchronously at load, while document.currentScript is still set.
     ensureSwatchStyles() runs later from an async callback, by which point
     currentScript is null — so the value has to be taken now. */
  var SELF_SRC = (document.currentScript && document.currentScript.src) || "";

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  /* ---------------------------------------------------------------------- */
  /* Cart helpers                                                            */
  /* ---------------------------------------------------------------------- */

  function refreshCartCount() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var n = cart.item_count;
        $$("[data-mm-cart-count]").forEach(function (el) {
          el.textContent = n > 0 ? n : "";
          /* The header bag link is named by aria-label="Bag", which REPLACES
             its content — so the count badge inside it was never announced.
             The count is folded into the name instead. */
          var bag = el.closest ? el.closest("a.mm-bag") : null;
          if (bag) {
            bag.setAttribute("aria-label", n > 0 ? "Bag, " + n + (n === 1 ? " item" : " items") : "Bag, empty");
          }
        });
        return cart;
      })
      .catch(function () { /* non-fatal */ });
  }

  function addToCart(variantId, quantity) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] })
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (e) { throw new Error(e.description || "Could not add to bag"); });
      return res.json();
    }).then(function (data) {
      refreshCartCount();
      return data;
    });
  }

  function flash(button, label, revertMs) {
    if (!button) return;
    var original = button.getAttribute("data-mm-label") || button.textContent;
    button.setAttribute("data-mm-label", original);
    button.textContent = label;
    window.setTimeout(function () { button.textContent = original; }, revertMs || 1600);
  }

  /* ---------------------------------------------------------------------- */
  /* Delegated clicks                                                        */
  /* ---------------------------------------------------------------------- */

  document.addEventListener("click", function (event) {
    var t = event.target;
    if (!t || !t.closest) return;

    /* -- announcement bar ------------------------------------------------ */
    var announceClose = t.closest("[data-mm-announce-close]");
    if (announceClose) {
      var bar = announceClose.closest("[data-mm-announce]");
      if (bar) bar.remove();
      try { sessionStorage.setItem("mm-announce-closed", "1"); } catch (e) {}
      syncHeaderHeight();
      return;
    }

    /* -- mobile drawer --------------------------------------------------- */
    if (t.closest("[data-mm-drawer-open]")) { setDrawer(true); return; }
    if (t.closest("[data-mm-drawer-close]") || t.closest(".mm-drawer-scrim")) { setDrawer(false); return; }

    /* Superseded by mm-drawer-accordion.js, which owns .mm-drawer-toggle on
       the capture phase. Unreachable for those clicks. */
    var drawerToggle = t.closest(".mm-drawer-toggle");
    if (drawerToggle) {
      var expanded = drawerToggle.getAttribute("aria-expanded") === "true";
      drawerToggle.setAttribute("aria-expanded", String(!expanded));
      var panel = drawerToggle.nextElementSibling;
      if (panel) panel.setAttribute("data-open", String(!expanded));
      return;
    }

    /* -- hero slides ----------------------------------------------------- */
    var heroNav = t.closest("[data-mm-hero-nav]");
    if (heroNav) {
      var hero = heroNav.closest("[data-mm-hero]");
      if (hero) stepHero(hero, Number(heroNav.getAttribute("data-mm-hero-nav")));
      return;
    }

    /* -- product card media carousel ------------------------------------- */
    var cardNav = t.closest("[data-mm-card-nav]");
    if (cardNav) {
      event.preventDefault();
      event.stopPropagation();
      var media = cardNav.closest("[data-mm-card-media]");
      if (media) stepCardMedia(media, Number(cardNav.getAttribute("data-mm-card-nav")));
      return;
    }

    /* -- quick add ------------------------------------------------------- */
    var quickAdd = t.closest("[data-mm-quickadd]");
    if (quickAdd) {
      event.preventDefault();
      event.stopPropagation();
      openQuickAdd(quickAdd.getAttribute("data-mm-quickadd"));
      return;
    }

    if (t.closest("[data-mm-modal-close]") || t.closest(".mm-modal-scrim")) { closeQuickAdd(); return; }

    /* -- size chips (quick add + PDP) ------------------------------------ */
    var sizeBtn = t.closest("[data-mm-size]");
    if (sizeBtn && !sizeBtn.disabled) {
      var group = sizeBtn.closest("[data-mm-size-group]");
      if (group) {
        $$("[data-mm-size]", group).forEach(function (b) { b.setAttribute("aria-pressed", "false"); });
        sizeBtn.setAttribute("aria-pressed", "true");
        var variantId = sizeBtn.getAttribute("data-mm-variant");
        var scope = group.closest("[data-mm-buy-scope]") || document;
        var input = $("[data-mm-variant-input]", scope);
        if (input) input.value = variantId;
        var submit = $("[data-mm-submit]", scope);
        if (submit) {
          submit.disabled = false;
          var ready = submit.getAttribute("data-mm-ready-label");
          if (ready) submit.textContent = ready;
        }
        var hint = $("[data-mm-size-hint]", scope);
        if (hint) hint.style.display = "none";
      }
      return;
    }

    /* -- quick add submit ------------------------------------------------ */
    var qaSubmit = t.closest("[data-mm-quickadd-submit]");
    if (qaSubmit && !qaSubmit.disabled) {
      var qaScope = qaSubmit.closest("[data-mm-buy-scope]");
      var qaField = qaScope ? $("[data-mm-variant-input]", qaScope) : null;
      var qaVariant = qaField ? qaField.value : null;
      if (!qaVariant) return;
      qaSubmit.disabled = true;
      addToCart(qaVariant, 1)
        .then(function () { flash(qaSubmit, "Added to bag"); setTimeout(closeQuickAdd, 700); })
        .catch(function (err) { flash(qaSubmit, err.message || "Sold out", 2400); })
        .then(function () { window.setTimeout(function () { qaSubmit.disabled = false; }, 800); });
      return;
    }

    /* -- quantity stepper ------------------------------------------------ */
    var qty = t.closest("[data-mm-qty]");
    if (qty) {
      var qtyWrap = qty.closest(".mm-qty");
      var qtyInput = qtyWrap ? $("input", qtyWrap) : null;
      if (qtyInput) {
        var next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + Number(qty.getAttribute("data-mm-qty")));
        qtyInput.value = next;
      }
      return;
    }

    /* -- accordions (PDP) ------------------------------------------------ *
     * Superseded by mm-accordion.js, which owns .mm-acc-head on the capture
     * phase and stops the event immediately, so this branch is unreachable
     * for those clicks. */
    var acc = t.closest(".mm-acc-head");
    if (acc) {
      var accOpen = acc.getAttribute("aria-expanded") === "true";
      var accGroup = acc.closest(".mm-acc");
      if (accGroup && !accOpen) {
        $$(".mm-acc-head", accGroup).forEach(function (h) {
          h.setAttribute("aria-expanded", "false");
          if (h.nextElementSibling) h.nextElementSibling.setAttribute("data-open", "false");
        });
      }
      acc.setAttribute("aria-expanded", String(!accOpen));
      if (acc.nextElementSibling) acc.nextElementSibling.setAttribute("data-open", String(!accOpen));
      return;
    }

    /* -- facet collapse (PLP sidebar) ------------------------------------ */
    var facet = t.closest(".mm-facet-head");
    if (facet) {
      var facetOpen = facet.getAttribute("aria-expanded") === "true";
      facet.setAttribute("aria-expanded", String(!facetOpen));
      return;
    }

    /* -- filter sidebar toggle ------------------------------------------- */
    var filterToggle = t.closest("[data-mm-filter-toggle]");
    if (filterToggle) {
      var body = $("[data-mm-plp-body]");
      if (body) {
        var on = body.getAttribute("data-filters") === "true";
        body.setAttribute("data-filters", String(!on));
        var lbl = $("[data-mm-filter-label]", filterToggle);
        if (lbl) lbl.textContent = on ? "Show Filters" : "Hide Filters";
        filterToggle.setAttribute("aria-expanded", String(!on));
        try { localStorage.setItem("mm-filters-open", String(!on)); } catch (e) {}
      }
      return;
    }

    /* -- shop this look --------------------------------------------------- */
    if (t.closest("[data-mm-look-open]")) { setLook(true); return; }
    if (t.closest("[data-mm-look-close]") || t.closest(".mm-look-scrim")) { setLook(false); return; }

    var lookAdd = t.closest("[data-mm-look-add]");
    if (lookAdd && !lookAdd.disabled) {
      lookAdd.disabled = true;
      addToCart(lookAdd.getAttribute("data-mm-look-add"), 1)
        .then(function () { flash(lookAdd, "Added"); })
        .catch(function () { flash(lookAdd, "Unavailable"); })
        .then(function () { window.setTimeout(function () { lookAdd.disabled = false; }, 900); });
      return;
    }

    var lookAll = t.closest("[data-mm-look-add-all]");
    if (lookAll && !lookAll.disabled) {
      var ids = (lookAll.getAttribute("data-mm-look-add-all") || "").split(",").filter(Boolean);
      if (!ids.length) return;
      lookAll.disabled = true;
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: ids.map(function (id) { return { id: Number(id), quantity: 1 }; }) })
      }).then(function () { refreshCartCount(); flash(lookAll, "Added to bag"); setTimeout(function () { setLook(false); }, 700); })
        .catch(function () { flash(lookAll, "Something went wrong"); })
        .then(function () { window.setTimeout(function () { lookAll.disabled = false; }, 900); });
      return;
    }
  });

  /* -- PDP add to bag ------------------------------------------------------ */
  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (!form.matches || !form.matches("[data-mm-product-form]")) return;
    event.preventDefault();
    var submit = $("[data-mm-submit]", form);
    var variant = $("[data-mm-variant-input]", form);
    var quantity = $("[data-mm-qty-input]", form);
    if (!variant || !variant.value) return;
    if (submit) submit.disabled = true;
    addToCart(variant.value, quantity ? parseInt(quantity.value, 10) || 1 : 1)
      .then(function () { flash(submit, "Added to bag"); })
      .catch(function (err) { flash(submit, err.message || "Unavailable", 2400); })
      .then(function () { window.setTimeout(function () { if (submit) submit.disabled = false; }, 900); });
  });

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;
    closeQuickAdd();
    setDrawer(false);
    setLook(false);
  });

  /* -- sort / facet auto-submit -------------------------------------------
     Changing the sort or ticking a facet reloads the page with the new
     results. With a mouse that is the expected shortcut; from the keyboard it
     is an unexpected change of context (WCAG 3.2.2). On Windows, arrowing
     through a closed <select> fires `change` for every option passed, so each
     keypress reloaded the page and the list could not be browsed at all.

     So the last input modality decides. A `change` that follows a pointer
     press submits at once, as before. One that follows a key press does not:
     the select submits on Enter, or on blur if its value has moved since it
     was focused; a facet checkbox submits on Enter, or when focus leaves the
     filter form with something changed — so a shopper can tick several sizes
     with Space and get one reload for all of them. */
  var lastInputWasKey = false;
  var lastKey = "";
  var pendingFilterSubmit = null;

  document.addEventListener("keydown", function (event) {
    /* Modifier-only presses (Cmd+click, Shift+click) are still pointer use. */
    if (event.key === "Shift" || event.key === "Control" || event.key === "Alt" || event.key === "Meta") return;
    lastInputWasKey = true;
    lastKey = event.key;
  }, true);
  document.addEventListener("pointerdown", function () { lastInputWasKey = false; lastKey = ""; }, true);

  function isAutoSubmit(el) {
    return !!(el && el.matches && (el.matches("[data-mm-sort]") || el.matches("[data-mm-facet-input]")));
  }

  function submitFilters(form) {
    pendingFilterSubmit = null;
    if (form) form.submit();
  }

  document.addEventListener("focusin", function (event) {
    var el = event.target;
    if (el && el.matches && el.matches("[data-mm-sort]")) el.setAttribute("data-mm-initial", el.value);
  });

  document.addEventListener("change", function (event) {
    var el = event.target;
    if (!isAutoSubmit(el)) return;
    var form = el.closest("form");
    if (!form) return;
    /* Enter that produced the change (choosing an option in an open list) is
       an explicit commit, so it applies straight away too. */
    if (!lastInputWasKey || lastKey === "Enter") { submitFilters(form); return; }
    pendingFilterSubmit = form;
  });

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Enter" || !isAutoSubmit(event.target)) return;
    /* Only when a keyboard change is waiting. With nothing pending, Enter is
       left to the browser (implicit form submission), which fires a real
       `submit` — the event mm-infinite-scroll.js hooks to carry filter
       parameters the form cannot represent. A pending change has already
       been carried: that script also runs on `change`. */
    if (!pendingFilterSubmit) return;
    event.preventDefault();
    submitFilters(pendingFilterSubmit);
  });

  document.addEventListener("focusout", function (event) {
    var el = event.target;
    if (!isAutoSubmit(el) || !pendingFilterSubmit) return;
    var form = pendingFilterSubmit;
    if (el.matches("[data-mm-sort]")) {
      if (el.value !== el.getAttribute("data-mm-initial")) submitFilters(form);
      return;
    }
    /* A facet: only apply once focus has left the form entirely, so Tabbing
       from one checkbox to the next does not reload between them. */
    var to = event.relatedTarget;
    if (to && form.contains(to)) return;
    submitFilters(form);
  });

  /* ---------------------------------------------------------------------- */
  /* Component behaviour                                                     */
  /* ---------------------------------------------------------------------- */

  /* -- dialog focus management ---------------------------------------------
     Shared by the mobile menu, Shop this look and Quick add — the same
     contract mm-cart.js follows for the cart drawer:

       closed  `inert`, so nothing inside is in the Tab order or the
               accessibility tree while it sits off-screen. (The panels only
               slide out of view; without this their links stayed tabbable.)
       open    focus moves in, Tab / Shift+Tab wrap inside it, and on close
               focus returns to whatever opened it.

     Only one of these can be open at a time, so a single slot tracks it. */

  var activeDialog = null;
  var dialogReturnFocus = null;

  function dialogStops(el) {
    return $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el)
      .filter(function (n) { return n.offsetParent !== null; });
  }

  function openDialog(el, focusTarget) {
    if (!el) return;
    if (activeDialog !== el) dialogReturnFocus = document.activeElement;
    activeDialog = el;
    el.inert = false;
    /* Next frame: the panel has just become visible, and focusing before
       layout can land on an element whose offsetParent is still null. */
    window.requestAnimationFrame(function () {
      var target = focusTarget || dialogStops(el)[0] || el;
      if (target && target.focus) target.focus({ preventScroll: true });
    });
  }

  function closeDialog(el) {
    if (!el) return;
    el.inert = true;
    if (activeDialog !== el) return;
    activeDialog = null;
    var back = dialogReturnFocus;
    dialogReturnFocus = null;
    if (back && document.contains(back) && back.focus) back.focus({ preventScroll: true });
  }

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Tab" || !activeDialog) return;
    var stops = dialogStops(activeDialog);
    if (!stops.length) { event.preventDefault(); return; }
    /* idx is -1 when focus is on the panel itself (it takes focus while Quick
       add loads) or has somehow left the dialog — wrap from there too. */
    var idx = stops.indexOf(document.activeElement);
    if (event.shiftKey && idx <= 0) { event.preventDefault(); stops[stops.length - 1].focus(); }
    else if (!event.shiftKey && (idx === -1 || idx === stops.length - 1)) { event.preventDefault(); stops[0].focus(); }
  });

  function setDrawer(open) {
    var root = $("[data-mm-drawer]");
    if (!root) return;
    var wasOpen = root.getAttribute("data-open") === "true";
    if (open === wasOpen) return;
    root.setAttribute("data-open", String(open));
    document.body.classList.toggle("mm-no-scroll", open);

    /* aria-modal only while open: left on a closed dialog, iOS VoiceOver can
       treat the rest of the page as unreachable. */
    var dialog = $('[role="dialog"]', root);
    if (dialog) {
      if (open) dialog.setAttribute("aria-modal", "true");
      else dialog.removeAttribute("aria-modal");
    }
    $$("[data-mm-drawer-open]").forEach(function (b) { b.setAttribute("aria-expanded", String(open)); });

    if (open) openDialog(root, $("[data-mm-drawer-close]", root));
    else closeDialog(root);
  }

  function setLook(open) {
    var panel = $("[data-mm-look]");
    var scrim = $("[data-mm-look-scrim]");
    if (!panel) return;
    var wasOpen = panel.getAttribute("data-open") === "true";
    if (open === wasOpen) return;
    panel.setAttribute("data-open", String(open));
    if (scrim) scrim.setAttribute("data-open", String(open));
    document.body.classList.toggle("mm-no-scroll", open);

    if (open) panel.setAttribute("aria-modal", "true");
    else panel.removeAttribute("aria-modal");
    $$("[data-mm-look-open]").forEach(function (b) { b.setAttribute("aria-expanded", String(open)); });

    if (open) openDialog(panel, $("[data-mm-look-close]", panel));
    else closeDialog(panel);
  }

  /* Inactive hero slides are only faded out, so their full-slide links and
     CTAs stayed in the Tab order — focus walked through slides the shopper
     could not see. `inert` takes them out of the Tab order and the
     accessibility tree together; the active slide and its copy are the only
     ones left live.

     WHY AN OBSERVER. The hero arrows, swipe and auto-advance are all stepped
     by the inline script in sections/mm_hero.liquid, on the capture phase —
     stepHero() below is unreachable for them. Watching data-active means this
     follows whichever script moved the slide, without either having to know
     about the other. The same pass keeps slide videos in step: only the
     visible slide plays (subject to playIfAllowed's vetoes). */
  function syncHero(hero) {
    $$("[data-mm-hero-slide], [data-mm-hero-copy]", hero).forEach(function (el) {
      var on = el.getAttribute("data-active") === "true";
      el.inert = !on;
      if (el.hasAttribute("data-mm-hero-slide")) {
        $$("video[data-mm-autoplay]", el).forEach(function (v) {
          if (on) playIfAllowed(v); else v.pause();
        });
      }
    });
  }

  function initHeroes() {
    $$("[data-mm-hero]").forEach(function (hero) {
      if ($$("[data-mm-hero-slide]", hero).length < 2) return;
      syncHero(hero);
      if (hero.__mmHeroObserved || !("MutationObserver" in window)) return;
      hero.__mmHeroObserved = true;
      new MutationObserver(function () { syncHero(hero); })
        .observe(hero, { subtree: true, attributes: true, attributeFilter: ["data-active"] });
    });
  }

  function stepHero(hero, delta) {
    var slides = $$("[data-mm-hero-slide]", hero);
    var copies = $$("[data-mm-hero-copy]", hero);
    if (slides.length < 2) return;
    var current = 0;
    slides.forEach(function (s, i) { if (s.getAttribute("data-active") === "true") current = i; });
    var next = (current + delta + slides.length) % slides.length;
    slides.forEach(function (s, i) { s.setAttribute("data-active", String(i === next)); });
    copies.forEach(function (c, i) {
      var on = i === next;
      c.setAttribute("data-active", String(on));
      if (on) {
        c.classList.remove("mm-animate-rise");
        void c.offsetWidth;
        c.classList.add("mm-animate-rise");
      }
    });
    syncHero(hero);
  }

  function stepCardMedia(media, delta) {
    var slides = $$("[data-mm-card-slide]", media);
    if (slides.length < 2) return;
    var current = 0;
    slides.forEach(function (s, i) { if (s.getAttribute("data-active") === "true") current = i; });
    var next = (current + delta + slides.length) % slides.length;
    slides.forEach(function (s, i) {
      var on = i === next;
      s.setAttribute("data-active", String(on));
      var video = s.querySelector("video");
      if (video) { if (on) { playIfAllowed(video); } else { video.pause(); } }
    });
  }

  function openQuickAdd(handle) {
    var modal = $("[data-mm-modal]");
    if (!modal || !handle) return;
    var panel = $("[data-mm-modal-panel]", modal);
    panel.innerHTML = '<p class="mm-eyebrow mm-muted" style="padding:2rem 0;text-align:center">Loading…</p>';
    panel.setAttribute("aria-busy", "true");
    if (!panel.hasAttribute("tabindex")) panel.setAttribute("tabindex", "-1");
    modal.setAttribute("data-open", "true");
    document.body.classList.add("mm-no-scroll");
    /* Focus the panel itself while loading, so a screen reader lands in the
       dialog straight away; it moves to the first control once content is in. */
    openDialog(modal, panel);

    fetch("/products/" + handle + "?view=mm-quickadd", { headers: { "X-Requested-With": "fetch" } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        panel.innerHTML = html;
        panel.removeAttribute("aria-busy");
        if (activeDialog === modal) {
          var first = dialogStops(panel)[0];
          if (first) first.focus({ preventScroll: true });
        }
      })
      .catch(function () {
        panel.innerHTML = '<p class="mm-eyebrow mm-muted" style="padding:2rem 0;text-align:center">Could not load this product.</p>';
        panel.removeAttribute("aria-busy");
      });
  }

  function closeQuickAdd() {
    var modal = $("[data-mm-modal]");
    if (!modal || modal.getAttribute("data-open") !== "true") return;
    modal.setAttribute("data-open", "false");
    document.body.classList.remove("mm-no-scroll");
    closeDialog(modal);
  }

  function syncHeaderHeight() {
    var root = $("[data-mm-header]");
    if (!root) return;
    var h = Math.round(root.getBoundingClientRect().height);
    document.documentElement.style.setProperty("--mm-header-h", h + "px");
  }

  function onScroll() {
    var root = $("[data-mm-header]");
    if (!root) return;
    root.setAttribute("data-scrolled", String(window.scrollY > 20));
  }

  /* ---------------------------------------------------------------------- */
  /* Init                                                                    */
  /* ---------------------------------------------------------------------- */

  var revealObserver = null;

  function initReveal() {
    if (!("IntersectionObserver" in window)) {
      $$(".mm-reveal").forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var delay = entry.target.getAttribute("data-mm-delay");
          if (delay) entry.target.style.animationDelay = delay + "ms";
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: "0px 0px -10% 0px", threshold: 0.08 });
    }
    $$(".mm-reveal:not(.is-visible)").forEach(function (el) { revealObserver.observe(el); });
  }

  function initCurtain() {
    var curtain = $("[data-mm-curtain]");
    if (!curtain) return;
    var seen = false;
    try { seen = sessionStorage.getItem("mm-curtain-seen") === "1"; } catch (e) {}
    if (seen) { curtain.remove(); return; }
    try { sessionStorage.setItem("mm-curtain-seen", "1"); } catch (e) {}
    window.setTimeout(function () { if (curtain.parentNode) curtain.remove(); }, 2100);
  }

  function initAnnounce() {
    var closed = false;
    try { closed = sessionStorage.getItem("mm-announce-closed") === "1"; } catch (e) {}
    if (!closed) return;
    var bar = $("[data-mm-announce]");
    if (bar) bar.remove();
  }

  /* Restores the shopper's last filter-drawer state. Runs on every navigation,
     because the server always renders the PLP with the drawer closed. */
  function initFilterPreference() {
    var body = $("[data-mm-plp-body]");
    if (!body) return;
    var stored = null;
    try { stored = localStorage.getItem("mm-filters-open"); } catch (e) {}
    if (stored === null) return;
    body.setAttribute("data-filters", stored);
    var lbl = $("[data-mm-filter-label]");
    if (lbl) lbl.textContent = stored === "true" ? "Hide Filters" : "Show Filters";
    $$("[data-mm-filter-toggle]").forEach(function (b) {
      b.setAttribute("aria-expanded", stored === "true" ? "true" : "false");
    });
  }

  /* Autoplay, subject to two vetoes:
       - prefers-reduced-motion: nothing starts on its own. The video is
         paused and left on its poster / first frame; a pause/play control
         (where the section has one) can still start it.
       - data-mm-user-paused="true": set by the hero / media-row / PDP pause
         buttons. A shopper who paused a video must not have it restarted by
         a re-init after navigation, a slide change or a section reload.
     data-mm-user-played="true" (set when the visitor presses Play) lifts the
     reduced-motion veto for that video: they asked for the motion. Same
     contract as mm-video-controls.js's shouldHold().
     Card videos carry data-mm-autoplay only on the active slide, and
     stepCardMedia() routes through here too, so they follow the same rules. */
  var reducedMotion = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;

  function motionReduced() { return !!(reducedMotion && reducedMotion.matches); }

  function playIfAllowed(video) {
    if (!video) return;
    video.muted = true;
    var held = video.getAttribute("data-mm-user-paused") === "true" ||
      (motionReduced() && video.getAttribute("data-mm-user-played") !== "true");
    if (held) {
      video.removeAttribute("autoplay");
      video.pause();
      return;
    }
    var play = video.play();
    if (play && play.catch) play.catch(function () {});
  }

  function initVideos() {
    $$("video[data-mm-autoplay]").forEach(function (video) {
      /* A card video on an inactive slide is not visible; leave it alone. */
      var slide = video.closest("[data-mm-card-slide], [data-mm-hero-slide]");
      if (slide && slide.getAttribute("data-active") !== "true") { video.pause(); return; }
      playIfAllowed(video);
    });
  }

  /* The preference can change while the page is open (macOS toggles it
     live); stop everything the moment it is switched on. */
  if (reducedMotion) {
    var onMotionPref = function () {
      if (!motionReduced()) { initVideos(); return; }
      $$("video[data-mm-autoplay]").forEach(function (v) {
        if (v.getAttribute("data-mm-user-played") !== "true") v.pause();
      });
    };
    if (reducedMotion.addEventListener) reducedMotion.addEventListener("change", onMotionPref);
    else if (reducedMotion.addListener) reducedMotion.addListener(onMotionPref);
  }

  /* ---------------------------------------------------------------------- */
  /* PDP colourway swatches                                                  */
  /* ---------------------------------------------------------------------- */

  function ensureSwatchStyles(host) {
    if ($("link[data-mm-swatch-css]")) return;

    var href = (host && host.getAttribute("data-css")) || "";
    if (!href && SELF_SRC) href = SELF_SRC.replace("mm-editorial.js", "mm-swatch.css");
    if (!href || href.indexOf("mm-swatch.css") === -1) return;

    var link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute("data-mm-swatch-css", "1");
    document.head.appendChild(link);
  }

  function initColorSiblings() {
    var host = $("[data-mm-colors]");
    if (!host || host.getAttribute("data-mm-done") === "1") return;
    host.setAttribute("data-mm-done", "1");

    var cfg = {
      parent: host.getAttribute("data-parent") || "",
      fabric: (host.getAttribute("data-fabric") || "").toLowerCase(),
      current: (host.getAttribute("data-current") || "").toLowerCase(),
      currentName: host.getAttribute("data-current") || "",
      hideSoldOut: host.getAttribute("data-hide-soldout") !== "false",
      hideSingle: host.getAttribute("data-hide-single") !== "false",
      newDays: parseInt(host.getAttribute("data-new-days"), 10) || 0,
      lowStock: parseInt(host.getAttribute("data-low-stock"), 10) || 0
    };

    /* The map is read for two things: the hex fill, and the ORDER.
     *
     * Without a canonical order the row is however the endpoint happened to
     * return that family, so the same style could show its colours in one
     * sequence here and another on the collection card. The map's line order is
     * the merchandising sequence and is the same on every product, so it makes
     * the row stable everywhere.
     */
    var map = {};
    var mapOrder = {};
    var seq = 0;
    (host.getAttribute("data-map") || "").split("\n").forEach(function (line) {
      var bits = line.split("|");
      if (bits.length < 2) return;
      var key = bits[0].trim().toLowerCase();
      if (!key) return;
      map[key] = bits[1].trim();
      if (!(key in mapOrder)) { mapOrder[key] = seq; seq += 1; }
    });
    cfg.order = mapOrder;

    fetchFamily()
      .then(function (colors) {
        return colors.length ? colors : fetchPredictive(cfg);
      })
      .catch(function () { return fetchPredictive(cfg); })
      .then(function (colors) { render(colors, cfg, map, host); })
      .catch(function () { /* leave the row hidden */ });
  }

  /* The colourway family, from the product's own `exchange-*` collection.
   *
   * This replaced a lookup that asked Shopify's SEARCH using a group key built
   * from the `YGroup_*` tag and the title base. Both were reconstructions of a
   * grouping the catalogue already records explicitly, and both inherited the
   * search index's blind spots: a product can be published to the Online Store
   * and absent from search, which is the state Bala, Colby, Moreland, Aliza and
   * Maaza in Fired Brick are in. The row came back holding every colourway
   * except the one on screen.
   *
   * The live site never had this problem because it never asked search — its
   * product-swatches.liquid walks the product's exchange collection. The
   * endpoint at templates/product.mm-swatches.liquid does the same, so both
   * sites now resolve colourways from one source of truth.
   */
  function fetchFamily() {
    var path = window.location.pathname.split("?")[0];
    var url = path + "?view=mm-swatches";
    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) { return (data && data.colors) || []; });
  }

  /* Last resort, for a style with no `exchange-*` collection at all.
     `unavailable_products=hide` is the one lever predictive search offers here
     — it used to be set to `last`, which is precisely why sold-out colourways
     stayed in the row. It cannot report stock or publish dates, so a row
     resolved this way carries no badges. */
  function fetchPredictive(cfg) {
    if (!cfg.parent) return Promise.resolve([]);
    var query = encodeURIComponent(cfg.parent + (cfg.fabric ? " " + cfg.fabric : ""));
    var unavailable = cfg.hideSoldOut ? "hide" : "last";
    var url = "/search/suggest.json?q=" + query +
      "&resources[type]=product&resources[limit]=10" +
      "&resources[options][unavailable_products]=" + unavailable;

    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        var parentKey = cfg.parent.toLowerCase();
        return products.filter(function (p) {
          var t = (p.title || "").toLowerCase();
          if (t.indexOf(" :: ") === -1) return false;
          if (t.indexOf(parentKey) === -1) return false;
          if (cfg.fabric && t.indexOf(cfg.fabric) === -1) return false;
          return true;
        }).map(function (p) {
          return {
            name: p.title.split(" :: ").pop().trim(),
            url: p.url,
            image: p.image || "",
            /* Predictive search omits `available` on some shop plans; treat a
               missing value as in stock, since the `hide` option above has
               already filtered the sold-out ones server-side. */
            available: p.available !== false,
            qty: 0,
            published: 0,
            badge: ""
          };
        });
      });
  }

  /* The colourway this page is actually showing, from the section's own markup.

     A floor, not the main mechanism: the endpoint should already return it.
     It covers a style with no `exchange-*` collection, where the family comes
     back empty and the row would otherwise lose the current colour entirely. */
  function withCurrent(colors, cfg) {
    if (!cfg.currentName) return colors;
    for (var i = 0; i < colors.length; i += 1) {
      if (colors[i] && colors[i].name && colors[i].name.toLowerCase() === cfg.current) {
        return colors;
      }
    }
    return colors.concat([{
      name: cfg.currentName,
      url: window.location.pathname,
      image: "",
      available: true,
      qty: 0,
      published: 0,
      badge: ""
    }]);
  }

  /* An explicit `Badge:` tag from the catalogue always wins. Otherwise New is
     tested before Last Few — a colour that has just landed in small numbers
     reads better as new than as nearly gone. */
  function badgeFor(color, cfg) {
    if (color.badge) return color.badge;
    var nowSec = Math.floor(Date.now() / 1000);
    if (cfg.newDays > 0 && color.published > 0) {
      var ageDays = (nowSec - color.published) / 86400;
      if (ageDays <= cfg.newDays) return "New";
    }
    if (cfg.lowStock > 0 && color.qty > 0 && color.qty <= cfg.lowStock) return "Last Few";
    return "";
  }

  /* The catalogue carries separate extended-size entries such as "Black 00 and
     20" holding their own stock. Keeping only the first hit would let a 1-unit
     size-run entry brand the main colourway "Last Few" while hundreds sit in
     the other record, so duplicates are merged: stock is summed, and
     availability and the earliest publish date carried over. */
  function dedupe(colors) {
    var byName = {};
    var order = [];

    colors.forEach(function (c) {
      if (!c || !c.name) return;
      var key = c.name.toLowerCase();
      var seen = byName[key];
      if (!seen) {
        byName[key] = {
          name: c.name,
          url: c.url,
          image: c.image,
          available: c.available !== false,
          qty: c.qty || 0,
          published: c.published || 0,
          badge: c.badge || ""
        };
        order.push(key);
        return;
      }
      /* Same record arriving twice must not double its stock. */
      if (c.url && c.url === seen.url) return;
      seen.qty += c.qty || 0;
      if (c.available !== false) {
        seen.available = true;
        if (!seen.url) seen.url = c.url;
      }
      if (!seen.image && c.image) seen.image = c.image;
      if (!seen.badge && c.badge) seen.badge = c.badge;
      if (c.published && (!seen.published || c.published < seen.published)) {
        seen.published = c.published;
      }
    });

    return order.map(function (k) { return byName[k]; });
  }

  /* Canonical swatch order: the colour map's line order, then anything not in
     the map alphabetically after it. Alphabetical alone would be stable but
     arbitrary — it would put Black next to Blush and split the neutrals. */
  function sortByMap(list, order) {
    var UNLISTED = 10000;
    return list.slice().sort(function (a, b) {
      var ai = order[a.name.toLowerCase()];
      var bi = order[b.name.toLowerCase()];
      if (ai === undefined) ai = UNLISTED;
      if (bi === undefined) bi = UNLISTED;
      if (ai !== bi) return ai - bi;
      return a.name.localeCompare(b.name);
    });
  }

  function render(colors, cfg, map, host) {
    if (!colors) return;

    var list = dedupe(withCurrent(colors, cfg)).filter(function (c) {
      /* The colour being viewed always stays in the row even when it is sold
         out — removing it would leave the swatch strip with no current state
         and no way back to where the shopper is standing. */
      if (cfg.hideSoldOut && c.available === false && c.name.toLowerCase() !== cfg.current) return false;
      return true;
    });

    if (!list.length) return;
    if (list.length < 2 && cfg.hideSingle) return;

    list = sortByMap(list, cfg.order || {});

    ensureSwatchStyles(host);

    var listEl = $("[data-mm-colors-list]", host);
    var nameEl = $("[data-mm-color-name]", host);
    if (!listEl) return;
    listEl.innerHTML = "";

    var anyBadge = false;

    list.forEach(function (c) {
      var key = c.name.toLowerCase();
      var isCurrent = key === cfg.current;
      var soldOut = c.available === false;
      var node = document.createElement(isCurrent || soldOut ? "span" : "a");
      if (!isCurrent && !soldOut) node.href = c.url;
      /* A span has no role, so an aria-label on it is ignored (and axe flags
         it as prohibited). role="img" gives the current and sold-out dots a
         name that screen readers read, with the state spelled out in it.
         aria-current stays for mm-shop.css's selected ring. */
      if (isCurrent || soldOut) node.setAttribute("role", "img");
      node.setAttribute("aria-current", String(isCurrent));
      node.setAttribute("title", c.name);
      node.setAttribute("aria-label", c.name + (isCurrent ? ", selected" : "") + (soldOut ? ", sold out" : ""));
      if (soldOut) node.setAttribute("data-mm-soldout", "true");

      var badge = badgeFor(c, cfg);
      if (badge) {
        node.setAttribute("data-mm-badge", badge);
        anyBadge = true;
      }

      if (map[key]) {
        node.style.backgroundColor = map[key];
      } else if (c.image) {
        node.style.backgroundImage = 'url("' + c.image + '")';
      } else {
        node.style.backgroundColor = "#d8d2c8";
      }

      node.addEventListener("mouseenter", function () { if (nameEl) nameEl.textContent = c.name; });
      node.addEventListener("mouseleave", function () {
        if (nameEl) nameEl.textContent = host.getAttribute("data-current");
      });
      listEl.appendChild(node);
    });

    listEl.setAttribute("data-has-badges", String(anyBadge));
    host.removeAttribute("hidden");
  }

  function mmInit() {
    window.__mmInitAt = Date.now();
    initAnnounce();
    initCurtain();
    syncHeaderHeight();
    onScroll();
    initReveal();
    initFilterPreference();
    initHeroes();
    initVideos();
    initColorSiblings();
    refreshCartCount();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mmInit);
  } else {
    mmInit();
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", syncHeaderHeight, { passive: true });
  document.addEventListener("shopify:section:load", mmInit);
  document.addEventListener("platter-cache-busted", mmInit);

  /* -- announce soft navigations ------------------------------------------
     Barba swaps <main> without a page load, so a screen reader hears nothing
     after a link is followed and keyboard focus is left on a link that no
     longer exists. After each swap: announce the new page title in a polite
     live region, and move focus to the page's h1 (or <main>) so Tab continues
     from the top of the new content. The focus target is not interactive, so
     its outline is suppressed; it only marks the reading position.

     Lives here rather than in _page-transitions.js because that file is only
     the source for Platter's bundled theme.js — editing it changes nothing. */
  function announceNavigation() {
    var region = document.getElementById("mm-route-announcer");
    if (!region) {
      region = document.createElement("div");
      region.id = "mm-route-announcer";
      region.className = "mm-sr";
      region.setAttribute("aria-live", "polite");
      region.setAttribute("aria-atomic", "true");
      document.body.appendChild(region);
    }
    var title = (document.title || "").split(/\s[–|-]\sM\.M\.\s?LaFleur/)[0].trim();
    /* Clear first, then set on the next frame, so the same title twice in a
       row is still read. */
    region.textContent = "";
    window.setTimeout(function () { region.textContent = title ? title + ", page loaded" : "Page loaded"; }, 100);

    var target = $("main h1") || $("main");
    if (target) {
      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      target.style.outline = "none";
      target.focus({ preventScroll: true });
    }
  }

  function afterNavigation() {
    mmInit();
    announceNavigation();
  }

  /* Barba swaps <main>; re-run init once the new container is in the DOM. */
  if (window.barba && window.barba.hooks) {
    window.barba.hooks.after(afterNavigation);
  } else {
    document.addEventListener("barba:after", afterNavigation);
  }

  window.mmInit = mmInit;
})();